import React, { useCallback, useContext } from 'react';
import { gql } from '@apollo/client';
import { toast } from 'react-toastify';
import Lottie from 'react-lottie-player';
import { Link } from 'react-router-dom';

import ModalRequestQuotation from 'Components/ModalRequestQuotation';
import Icon from 'Components/Icon';
import AmountPaid from 'Components/AmountPaid';
import TableWithInvoice from './components/TableWithInvoice/TableWithInvoice';
import animationData from 'Shared/spiner_blue.json';
import getTotalAmountPaid from 'Shared/util/getTotalAmountPaid';

import emptyLogo from '../../assets/QuatationsEmpty.svg';
import emptyLogoDark from '../../assets/QuatationsEmptyDark.svg';
import EmptyPage from 'Components/EmptyPage/EmptyPage';

import StoreContext from 'Shared/context/StoreContext';
import useQueryByContract from 'Shared/hooks/useQueryByContract';
import withErrorBoundary from 'Components/ErrorBoundary/ErrorBoundary';
import { logger } from 'RootShared/utility/ErrorLogger';
import useDocumentTitle from 'Shared/hooks/useDocumentTitle';

export const queryGetCompletedOrders = gql`
  query TabQuotationsQuery($contractId: Int!) @api(name: root) {
    orders(status: PAID, contractId: $contractId) {
      id
      reference
      subject
      createdAt
      invoices {
        id
        reference
      }
      total
      pdfUrl
      currency
    }
  }
`;

const TabCompleted = () => {
  const { contract } = useContext(StoreContext);
  const { loading, data, error } = useQueryByContract(contract, queryGetCompletedOrders);

  useDocumentTitle('Orders - Completed');

  const formatingForTable = useCallback((orders) => {
    let ordersForTable = [];

    ordersForTable = orders.map(({ reference, subject, createdAt, total, currency, pdfUrl, invoices, id }) => {
      return {
        order: { reference: reference, slug: subject },
        issued: { date: createdAt },
        total: { number: total, currency: currency },
        download: { url: pdfUrl },
        invoices: invoices,
        id: id,
      };
    });

    return ordersForTable;
  }, []);

  const getOrdersComponent = useCallback(
    (data) => {
      if (data.orders.length === 0) {
        return (
          <div data-testid="tab-completed-empty-page">
            <EmptyPage
              img={
                <img src={JSON.parse(localStorage.getItem('nightMode')) ? emptyLogoDark : emptyLogo} alt="empty logo" />
              }
              title={'No completed orders yet'}
              text={"Looks like you haven't had paid invoices yet."}
            />
          </div>
        );
      }

      return (
        <>
          <AmountPaid title={'Total amount'} amount={getTotalAmountPaid(data.orders)} />
          <TableWithInvoice orders={formatingForTable(data.orders)} />
        </>
      );
    },
    [formatingForTable]
  );

  if (error) {
    logger.error(error);
    toast(error.message, {
      type: toast.TYPE.ERROR,
      autoClose: 3000,
      closeButton: true,
      hideProgressBar: false,
    });
  }

  return (
    <>
      <div className="container orders " data-testid="tab-completed">
        <div className="tab-completed">
          <div className="tab-group-control-header">
            <Link
              className="tab-group-control-header__btn tab-group-control-header__btn--contained"
              to="/customer/orders/completed/?request_quotation"
            >
              <Icon name="icon-plus" />
              <span data-testid="request-quotation-completed">Request a quotation</span>
            </Link>
          </div>
          {loading && (
            <Lottie
              className="lottie"
              loop={true}
              play={true}
              animationData={animationData}
              width={100}
              style={{ width: 100, height: 100, margin: '0px auto' }}
            />
          )}
          {data && getOrdersComponent(data)}
        </div>
      </div>
      <ModalRequestQuotation />
    </>
  );
};

export default withErrorBoundary(TabCompleted);
